import { Table, Checkbox, Text, Button, Flex, TextField } from '@radix-ui/themes';
import { useQuery } from '@apollo/client';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebounce } from '../../../../hooks/useDebounce';
import { getManagerAccessToken } from '../../../accesstoken';
import { Pagination } from '../../../elements/Pagination';
import type {
  QuestionQueryResponse,
  QuestionItem,
  QuestionPaginatedResult,
  QuestionQueryError,
} from './types/questionItem';
import { PAGINATED_QUESTIONS } from '../../query/paginatedQuestions';

const PAGE_SIZE = 20;

export const QuestionPaginatedTable = ({
  idExisting,
  setQuestionsIdsToSave,
}: {
  idExisting: { resourceId: string; type: string }[];
  setQuestionsIdsToSave: React.Dispatch<React.SetStateAction<{ resourceId: string; type: string }[]>>;
}) => {
  const { t } = useTranslation();
  const [pageIndex, setPageIndex] = useState(0);
  const createKey = (resourceId: string, type: string) => `${type}:${resourceId}`;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(idExisting.map(({ resourceId, type }) => createKey(resourceId, type))),
  );
  const [searchValue, setSearchValue] = useState('');
  const debounceSearch = useDebounce((s) => setSearchValue(s), 500);
  const token = getManagerAccessToken();
  const { data, loading } = useQuery<{ questionsv2: QuestionQueryResponse }>(PAGINATED_QUESTIONS, {
    variables: {
      input: {
        token: token,
        filter: {
          text: searchValue,
          questionType: [],
          language: [],
          scoringCategory: [],
          starQuestion: false,
          visibility: ['local'],
        },
        pageIndex: pageIndex ?? 0,
        pageSize: PAGE_SIZE,
        sortingState: [{ id: 'created_at', desc: true }],
      },
    },
  });

  const item = data?.questionsv2?.item;

  const questions: QuestionItem[] =
    item?.__typename === 'QuestionPaginatedResult' ? ((item as QuestionPaginatedResult).data ?? []) : [];

  const totalPages: number =
    item?.__typename === 'QuestionPaginatedResult' ? ((item as QuestionPaginatedResult).totalPages ?? 1) : 1;

  useEffect(() => {
    if (item?.__typename === 'QuestionQueryError') {
      toast.error(`GraphQL error: ${(item as QuestionQueryError).message}`);
    }
  }, [item]);

  const isSelected = (resourceId: string, type: string) => {
    return selectedIds.has(createKey(resourceId, type));
  };

  const toggleSelect = (resourceId: string, type: string) => {
    const key = createKey(resourceId, type);
    setSelectedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) newSet.delete(key);
      else newSet.add(key);
      return newSet;
    });
  };

  const areAllRowsOnPageSelected =
    questions.length > 0 && questions.every((t) => selectedIds.has(createKey(t.id, 'question')));

  const toggleSelectAll = () => {
    const newSet = new Set(selectedIds);
    if (areAllRowsOnPageSelected) {
      questions.forEach((t) => {
        newSet.delete(createKey(t.id, 'question'));
      });
    } else {
      questions.forEach((t) => {
        newSet.add(createKey(t.id, 'question'));
      });
    }
    setSelectedIds(newSet);
  };

  const handlePageChange = (page: number) => {
    setPageIndex(page - 1);
  };

  useEffect(() => {
    setQuestionsIdsToSave([
      ...Array.from(selectedIds).map((key) => {
        const [type, resourceId] = key.split(':');
        return { resourceId, type };
      }),
    ]);
  }, [selectedIds]);

  return (
    <>
      <Flex mb="4" align="center" gap="3">
        <TextField.Root
          placeholder={t('settings.accessGroupv2.questionTable.searchPlaceholder')}
          type="text"
          className="w-full"
          size="3"
          defaultValue={searchValue}
          onChange={(e) => {
            console.log(e.target.value);
            debounceSearch(e.target.value);
          }}
        />
      </Flex>
      <Table.Root variant="surface" size="2" layout="auto">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell width="1">
              <Checkbox checked={areAllRowsOnPageSelected} onCheckedChange={toggleSelectAll} />
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.accessGroupv2.questionTable.columns.name')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.accessGroupv2.questionTable.columns.type')}</Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>
              {t('settings.accessGroupv2.questionTable.columns.languages')}
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell>{t('settings.accessGroupv2.questionTable.columns.id')}</Table.ColumnHeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {loading && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text size="2">{t('settings.accessGroupv2.questionTable.loading')}</Text>
              </Table.Cell>
            </Table.Row>
          )}

          {!loading && questions.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={6}>
                <Text size="2">{t('settings.accessGroupv2.questionTable.empty')}</Text>
              </Table.Cell>
            </Table.Row>
          )}

          {!loading &&
            questions.map((Question) => (
              <Table.Row key={Question.id}>
                <Table.Cell>
                  <Checkbox
                    checked={isSelected(Question.id, 'question')}
                    onCheckedChange={() => toggleSelect(Question.id, 'question')}
                  />
                </Table.Cell>
                <Table.Cell>
                  {Question.shortDescription} {Question.text}
                </Table.Cell>
                <Table.Cell>{Question.type}</Table.Cell>
                <Table.Cell>{Question.availableLanguages}</Table.Cell>
                <Table.Cell>{Question.id ?? '-'}</Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table.Root>
      {selectedIds.size > 0 && (
        <Flex justify="between" align="center" px="4" py="3" mt="2">
          <Text size="2" weight="medium">
            {selectedIds.size === 1
              ? t('settings.accessGroupv2.questionTable.selectedCountSingular', { count: selectedIds.size })
              : t('settings.accessGroupv2.questionTable.selectedCountPlural', { count: selectedIds.size })}
          </Text>
          <Button variant="ghost" color="gray" onClick={() => setSelectedIds(new Set())}>
            {t('settings.accessGroupv2.questionTable.clearSelectionButton')}
          </Button>
        </Flex>
      )}
      <Flex justify="center" align="center" gap="2" my="4">
        <Pagination totalPages={totalPages} currentPage={pageIndex + 1} onPageChange={handlePageChange} />
      </Flex>
    </>
  );
};
